import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { Modal } from '@/components/molecules/Modal';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { usePurchasesReport, useExportReportMutation, useWarehousesList } from '@/hooks/useErpApi';
import type { PurchaseReportFilter, PurchaseReportRecord } from '@kisanlink/erp-service';

export default function PurchasesReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<PurchaseReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedRecord, setSelectedRecord] = useState<PurchaseReportRecord & { 
    expected_delivery?: string;
    actual_delivery?: string;
    grn_id?: string;
    received_quantity?: number;
    accepted_quantity?: number;
    rejected_quantity?: number;
    rejection_rate?: number;
    total_rejected_value?: number;
    acceptance_rate_percent?: number;
  } | null>(null);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const queryFilters: PurchaseReportFilter = {
    ...filters,
    limit: itemsPerPage,
    offset,
    ...(activeTab !== 'all' && { status: activeTab }),
  };

  const { data: report, isLoading, error, refetch } = usePurchasesReport(queryFilters);
  const { data: warehouses } = useWarehousesList({});
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const warehouseOptions = (warehouses || []).map((w) => ({ id: w.id, name: w.name }));

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as PurchaseReportFilter);
    setCurrentPage(1);
  }, []);
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  
  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({
          reportType: 'purchases',
          filters: { ...queryFilters, format },
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `purchases-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [queryFilters, exportReport]
  );

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }
    > = {
      draft: { variant: 'neutral', label: 'Draft' },
      placed: { variant: 'info', label: 'Placed' },
      confirmed: { variant: 'info', label: 'Confirmed' },
      verified: { variant: 'success', label: 'Verified' },
      paid: { variant: 'success', label: 'Paid' },
      out_for_delivery: { variant: 'warning', label: 'In Transit' },
      delivered: { variant: 'success', label: 'Delivered' },
      cancelled: { variant: 'error', label: 'Cancelled' },
    };
    const config = statusMap[status] || { variant: 'neutral' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'error'; label: string }> = {
      paid: { variant: 'success', label: 'Paid' },
      partial: { variant: 'warning', label: 'Partial' },
      unpaid: { variant: 'error', label: 'Unpaid' },
    };
    const config = statusMap[status] || { variant: 'warning' as const, label: status };
    return (
      <Badge variant={config.variant} size="sm">
        {config.label}
      </Badge>
    );
  };

  const columns: TableColumn<PurchaseReportRecord>[] = [
    {
      key: 'po_number',
      header: 'PO Number',
      className: 'min-w-[120px]',
      render: (row) => (
        <span className="font-mono text-sm font-medium text-neutral-900 whitespace-nowrap">{row.po_number}</span>
      ),
    },
    {
      key: 'collaborator_name',
      header: 'Vendor',
      render: (row) => (
        <div>
          <div className="font-medium text-neutral-900">{row.collaborator_name}</div>
          <div className="text-xs text-neutral-500">{row.warehouse_name}</div>
        </div>
      ),
    },
    {
      key: 'order_date',
      header: 'Order Date',
      render: (row) => new Date(row.order_date).toLocaleDateString(),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: 'payment_status',
      header: 'Payment',
      render: (row) => getPaymentBadge(row.payment_status),
    },
    {
      key: 'total_amount',
      header: 'Amount',
      render: (row) => (
        <span className="font-medium text-neutral-900">₹{row.total_amount.toLocaleString()}</span>
      ),
    },
    {
      key: 'outstanding_amount',
      header: 'Outstanding',
      render: (row) => (
        <span
          className={row.outstanding_amount > 0 ? 'text-error-600 font-medium' : 'text-neutral-500'}
        >
          ₹{row.outstanding_amount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'item_count',
      header: 'Items',
      render: (row) => row.item_count,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedRecord(row as PurchaseReportRecord & { 
            expected_delivery?: string;
            actual_delivery?: string;
            grn_id?: string;
            received_quantity?: number;
            accepted_quantity?: number;
            rejected_quantity?: number;
            rejection_rate?: number;
            total_rejected_value?: number;
            acceptance_rate_percent?: number;
          })}
          className="text-neutral-600 hover:text-primary-600"
        >
          <Icon name="Eye" size={16} />
        </Button>
      ),
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Orders',
          value: summary.total_orders,
          icon: 'ShoppingBag' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Amount',
          value: summary.total_amount,
          icon: 'IndianRupee' as const,
          color: 'info' as const,
          format: 'currency' as const,
        },
        {
          label: 'Paid Amount',
          value: summary.paid_amount,
          icon: 'CircleCheck' as const,
          color: 'success' as const,
          format: 'currency' as const,
        },
        {
          label: 'Outstanding',
          value: summary.outstanding_amount,
          icon: 'CircleAlert' as const,
          color: 'warning' as const,
          format: 'currency' as const,
        },
      ]
    : [];

  // Pagination is handled by the Pagination component below

  const statusTabs = [
    { key: 'all', label: 'All' },
    { key: 'placed', label: 'Placed' },
    { key: 'verified', label: 'Verified' },
    { key: 'paid', label: 'Paid' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Purchases Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Purchase order history with payment status and vendor breakdown
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={true}
        showSearch={true}
        showWarehouse={true}
        warehouses={warehouseOptions}
        searchPlaceholder="Search by PO number..."
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Status Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="flex gap-2 flex-wrap">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Status Breakdown */}
      {!isLoading && !error && summary && summary.by_status && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-sm font-semibold text-neutral-700 mb-4">Status Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <div className="bg-neutral-100 p-2 rounded-lg">
                  <Icon name="ShoppingBag" size={20} className="text-neutral-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-500 capitalize">{status}</p>
                  <p className="text-lg font-semibold text-neutral-900">{count || 0}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No purchase orders found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        title="Purchase Order Details"
        size="lg"
      >
        {selectedRecord && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500 mb-1">PO ID</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">PO Number</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.po_number}</p>
              </div>
              {(selectedRecord as any).external_order_id && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">External Order ID</p>
                  <p className="text-sm font-medium text-neutral-900 font-mono">{(selectedRecord as any).external_order_id}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-neutral-500 mb-1">Vendor</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.collaborator_name}</p>
                <p className="text-xs text-neutral-500 font-mono">{selectedRecord.collaborator_id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Warehouse</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.warehouse_name}</p>
                <p className="text-xs text-neutral-500 font-mono">{selectedRecord.warehouse_id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Order Date</p>
                <p className="text-sm text-neutral-900">{new Date(selectedRecord.order_date).toLocaleDateString()}</p>
              </div>
              {selectedRecord.expected_delivery && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Expected Delivery</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.expected_delivery).toLocaleDateString()}</p>
                </div>
              )}
              {selectedRecord.actual_delivery && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Actual Delivery</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.actual_delivery).toLocaleDateString()}</p>
                </div>
              )}
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Status Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Order Status</p>
                  {getStatusBadge(selectedRecord.status)}
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Payment Status</p>
                  {getPaymentBadge(selectedRecord.payment_status)}
                </div>
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Financial Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total Amount</p>
                  <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.total_amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Paid Amount</p>
                  <p className="text-sm font-medium text-success-700">₹{selectedRecord.paid_amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Outstanding Amount</p>
                  <p className={`text-sm font-medium ${selectedRecord.outstanding_amount > 0 ? 'text-error-600' : 'text-neutral-500'}`}>
                    ₹{selectedRecord.outstanding_amount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Item Count</p>
                  <p className="text-sm font-medium text-neutral-900">{selectedRecord.item_count}</p>
                </div>
              </div>
            </div>

            {(selectedRecord.received_quantity !== undefined || selectedRecord.accepted_quantity !== undefined || selectedRecord.rejected_quantity !== undefined) && (
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Receipt Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedRecord.grn_id && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">GRN ID</p>
                      <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.grn_id}</p>
                    </div>
                  )}
                  {selectedRecord.received_quantity !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Received Quantity</p>
                      <p className="text-sm font-medium text-neutral-900">{selectedRecord.received_quantity.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.accepted_quantity !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Accepted Quantity</p>
                      <p className="text-sm font-medium text-success-700">{selectedRecord.accepted_quantity.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.rejected_quantity !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Rejected Quantity</p>
                      <p className="text-sm font-medium text-error-600">{selectedRecord.rejected_quantity.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.rejection_rate !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Rejection Rate</p>
                      <p className="text-sm font-medium text-neutral-900">{selectedRecord.rejection_rate.toFixed(2)}%</p>
                    </div>
                  )}
                  {selectedRecord.acceptance_rate_percent !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Acceptance Rate</p>
                      <p className="text-sm font-medium text-success-700">{selectedRecord.acceptance_rate_percent.toFixed(2)}%</p>
                    </div>
                  )}
                  {selectedRecord.total_rejected_value !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Total Rejected Value</p>
                      <p className="text-sm font-medium text-error-600">₹{selectedRecord.total_rejected_value.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Timestamps</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Created At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Updated At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.updated_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
